import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  HackerChecklistInput,
  HackerChecklistSection,
  HackerChecklistItem,
  HackerChecklistDifficulty,
  HackerChecklistStatus
} from '../../lib/hackerChecklistsService';

interface HackerChecklistEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: HackerChecklistInput) => Promise<void> | void;
  initialData?: Partial<HackerChecklistInput>;
  isNew?: boolean;
}

const createItem = (): HackerChecklistItem => ({
  id: `item-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  text: '',
  referenceUrl: '',
  toolHint: '',
  required: false
});

const createSection = (): HackerChecklistSection => ({
  id: `section-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  title: '',
  objective: '',
  items: [createItem()]
});

const statusOptions: HackerChecklistStatus[] = ['Draft', 'Published'];
const difficultyOptions: HackerChecklistDifficulty[] = ['Beginner', 'Intermediate', 'Advanced'];

export const HackerChecklistEditDialog: React.FC<HackerChecklistEditDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  isNew = false
}) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [category, setCategory] = useState(initialData?.category || 'general');
  const [tags, setTags] = useState((initialData?.tags || []).join(', '));
  const [status, setStatus] = useState<HackerChecklistStatus>(initialData?.status || 'Draft');
  const [difficulty, setDifficulty] = useState<HackerChecklistDifficulty | ''>(initialData?.difficulty || '');
  const [sections, setSections] = useState<HackerChecklistSection[]>(
    initialData?.sections && initialData.sections.length > 0
      ? initialData.sections
      : [createSection()]
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initialData?.title || '');
      setDescription(initialData?.description || '');
      setCategory(initialData?.category || 'general');
      setTags((initialData?.tags || []).join(', '));
      setStatus(initialData?.status || 'Draft');
      setDifficulty(initialData?.difficulty || '');
      setSections(
        initialData?.sections && initialData.sections.length > 0
          ? initialData.sections
          : [createSection()]
      );
    }
  }, [open, initialData]);

  const sectionCount = sections.length;
  const totalItems = useMemo(
    () => sections.reduce((total, section) => total + section.items.length, 0),
    [sections]
  );

  const handleAddSection = () => {
    setSections((prev) => [...prev, createSection()]);
  };

  const handleRemoveSection = (sectionId: string) => {
    setSections((prev) => prev.filter((section) => section.id !== sectionId));
  };

  const handleSectionChange = (sectionId: string, updates: Partial<HackerChecklistSection>) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId ? { ...section, ...updates } : section
      )
    );
  };

  const handleAddItem = (sectionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, items: [...section.items, createItem()] }
          : section
      )
    );
  };

  const handleRemoveItem = (sectionId: string, itemId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, items: section.items.filter((item) => item.id !== itemId) }
          : section
      )
    );
  };

  const handleItemChange = (
    sectionId: string,
    itemId: string,
    updates: Partial<HackerChecklistItem>
  ) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              items: section.items.map((item) =>
                item.id === itemId ? { ...item, ...updates } : item
              )
            }
          : section
      )
    );
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Title is required');
      return;
    }

    if (sections.some((section) => !section.title.trim())) {
      alert('All sections require a title');
      return;
    }

    if (sections.some((section) => section.items.length === 0)) {
      alert('Each section must contain at least one checklist item');
      return;
    }

    setSaving(true);
    const normalizedTags = tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

    const payload: HackerChecklistInput = {
      title: title.trim(),
      description: description.trim(),
      category: category.trim() || 'general',
      tags: normalizedTags,
      status,
      difficulty: (difficulty as HackerChecklistDifficulty) || undefined,
      sections: sections.map((section) => ({
        ...section,
        title: section.title.trim(),
        objective: section.objective?.trim() || '',
        items: section.items.map((item) => ({
          ...item,
          text: item.text.trim(),
          referenceUrl: item.referenceUrl?.trim() || '',
          toolHint: item.toolHint?.trim() || ''
        }))
      }))
    };

    try {
      await onSave(payload);
      onClose();
    } catch (error) {
      console.error('Failed to save hacker checklist:', error);
      alert('Failed to save checklist. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (!value ? onClose() : null)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-4xl">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Create Hacker Checklist' : 'Edit Hacker Checklist'}</DialogTitle>
          <DialogDescription>
            {sectionCount} section{sectionCount !== 1 ? 's' : ''} • {totalItems} item{totalItems !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Title</label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Recon Playbook" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Category</label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="bounty" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Status</label>
              <select
                className="w-full border rounded-md h-10 px-3"
                value={status}
                onChange={(e) => setStatus(e.target.value as HackerChecklistStatus)}
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Difficulty</label>
              <select
                className="w-full border rounded-md h-10 px-3"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as HackerChecklistDifficulty)}
              >
                <option value="">Not specified</option>
                {difficultyOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Tags (comma separated)</label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="recon, bug bounty, web" />
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short summary of what this checklist covers"
              rows={3}
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">Sections</h3>
              <Button variant="outline" onClick={handleAddSection}>
                + Add Section
              </Button>
            </div>

            {sections.map((section, sectionIndex) => (
              <div key={section.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                  <div className="flex-1 space-y-2">
                    <div>
                      <label className="text-sm font-medium mb-1 block">Section Title</label>
                      <Input
                        value={section.title}
                        onChange={(e) => handleSectionChange(section.id, { title: e.target.value })}
                        placeholder={`Section ${sectionIndex + 1}`}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">Objective (optional)</label>
                      <Input
                        value={section.objective || ''}
                        onChange={(e) => handleSectionChange(section.id, { objective: e.target.value })}
                        placeholder="Goal of this section"
                      />
                    </div>
                  </div>
                  {sections.length > 1 && (
                    <Button variant="destructive" onClick={() => handleRemoveSection(section.id)}>
                      Remove Section
                    </Button>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold">Checklist Items</h4>
                    <Button variant="secondary" onClick={() => handleAddItem(section.id)}>
                      + Add Item
                    </Button>
                  </div>

                  {section.items.map((item) => (
                    <div key={item.id} className="border rounded-md p-3 space-y-2 bg-gray-50">
                      <div className="flex flex-col md:flex-row gap-2">
                        <div className="flex-1">
                          <label className="text-xs font-medium mb-1 block">Checklist Text</label>
                          <Textarea
                            rows={2}
                            value={item.text}
                            onChange={(e) => handleItemChange(section.id, item.id, { text: e.target.value })}
                            placeholder="Describe the action to perform"
                          />
                        </div>
                        <div className="md:w-32">
                          <label className="text-xs font-medium mb-1 block">Required</label>
                          <select
                            className="w-full border rounded-md h-10 px-2"
                            value={item.required ? 'yes' : 'no'}
                            onChange={(e) => handleItemChange(section.id, item.id, { required: e.target.value === 'yes' })}
                          >
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium mb-1 block">Reference URL (optional)</label>
                          <Input
                            value={item.referenceUrl || ''}
                            onChange={(e) => handleItemChange(section.id, item.id, { referenceUrl: e.target.value })}
                            placeholder="https://..."
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium mb-1 block">Tool Hint / Command (optional)</label>
                          <Input
                            value={item.toolHint || ''}
                            onChange={(e) => handleItemChange(section.id, item.id, { toolHint: e.target.value })}
                            placeholder="e.g. nmap -sV target.com"
                          />
                        </div>
                      </div>
                      {section.items.length > 1 && (
                        <div className="flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveItem(section.id, item.id)}>
                            Remove Item
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create Checklist' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HackerChecklistEditDialog;

